// Party validation rules (our rules, EFM-agnostic)
/**
 * Collect the local ids of parties that are the FILING_PARTY of a document whose
 * doc-level SELF_REP metadata is set. Feeds `docSelfRepPartyIds` above (R6) so an
 * explicitly self-rep filing still requires self-rep contact even when the
 * party's derived `isSelfRepresented` flag is false. Mirrors the envelope's
 * totality (stage-3-envelope.ts): SELF_REP is truthy as boolean `true` or the
 * string `'true'`, and FILING_PARTY is a string array of local party ids
 * (object-array new-data payloads and missing values are ignored). Joins on the
 * explicit FILING_PARTY reference only — no plaintiff-role default.
 *
 * Lives here beside the input type it builds, so every caller that assembles a
 * `PartyValidationInput` — `validate.ts`'s tab rules and the guided-section
 * readiness predicates alike — shares this one derivation rather than mirroring
 * it. That placement is also what keeps `section-readiness.ts` off `validate.ts`.
 */
export function collectDocSelfRepPartyIds(documents) {
    const ids = new Set();
    for (const doc of documents) {
        const mv = doc.metadataValues ?? {};
        if (mv.SELF_REP !== true && mv.SELF_REP !== 'true')
            continue;
        const filingParty = mv.FILING_PARTY;
        if (!Array.isArray(filingParty))
            continue;
        for (const id of filingParty) {
            if (typeof id === 'string')
                ids.add(id);
        }
    }
    return ids;
}
/**
 * Validate parties and attorneys.
 *
 * Rules (ours):
 * - At least one party is required
 * - Exactly one lead client is required
 * - An attorney or self-represented party is required
 */
export function validateParties(input) {
    const errors = [];
    const { parties, attorneys } = input;
    const docSelfRep = new Set(input.docSelfRepPartyIds ?? []);
    if (parties.length === 0) {
        errors.push({
            field: 'parties',
            rule: 'min-parties',
            message: 'At least one party is required',
            source: 'ours',
        });
    }
    const leadClients = parties.filter(p => p.isLeadClient);
    if (leadClients.length !== 1) {
        errors.push({
            field: 'parties',
            rule: 'one-lead-client',
            message: 'Exactly one lead client is required',
            source: 'ours',
        });
    }
    const hasSelfRep = parties.some(p => p.isSelfRepresented);
    if (!hasSelfRep && attorneys.length === 0) {
        errors.push({
            field: 'attorneys',
            rule: 'attorney-or-self-rep',
            message: 'An attorney or self-represented party is required',
            source: 'ours',
        });
    }
    const orgPlaintiffRoles = ['PLAIN', 'PET'];
    for (const party of parties) {
        if (party.partyType === 'organization' &&
            orgPlaintiffRoles.includes(party.roleCode)) {
            const hasLinkedAttorney = attorneys.some(att => att.representedPartyIds?.includes(party.id));
            if (!hasLinkedAttorney) {
                errors.push({
                    field: 'parties',
                    rule: 'org-plaintiff-requires-attorney',
                    message: 'Organizations filing as plaintiff must be represented by an attorney. Add an attorney and link them to this party.',
                    source: 'ours',
                });
            }
        }
    }
    // Validate attorney fields
    const selfRepParty = (p) => p.isSelfRepresented || docSelfRep.has(p.id);
    const anySelfRepParty = parties.some(selfRepParty);
    for (let i = 0; i < attorneys.length; i++) {
        const att = attorneys[i];
        // In-pro-per carve-out (Walter FWR-9/FWR-10): no bar number is required when
        // the entry stands in for a self-represented filer — every party it links to
        // is self-rep, or it links to no one on a filing that has a self-rep party
        // (the extraction path mints the "ATTORNEY OR PARTY WITHOUT ATTORNEY" box as
        // a bar-less, link-less attorney). The submit pipeline emits the bar element
        // conditionally, so a bar-less entry is already valid downstream (the
        // certified Madera self-rep envelopes carried none).
        const linkedParties = (att.representedPartyIds ?? [])
            .map(id => parties.find(p => p.id === id))
            .filter((p) => p !== undefined);
        const inProPer = linkedParties.length > 0
            ? linkedParties.every(selfRepParty)
            : anySelfRepParty;
        if (!att.barNumber && !inProPer) {
            errors.push({
                field: `attorneys[${i}].barNumber`,
                rule: 'attorney-bar-required',
                message: `Attorney "${att.firstName || ''} ${att.lastName || ''}" must have a bar number`,
                source: 'ours',
            });
        }
        if (!att.lastName) {
            errors.push({
                field: `attorneys[${i}].lastName`,
                rule: 'attorney-name-required',
                message: 'Attorney last name is required',
                source: 'ours',
            });
        }
        // Validate representedPartyIds reference valid parties
        if (att.representedPartyIds?.length) {
            for (const partyId of att.representedPartyIds) {
                if (!parties.find(p => p.id === partyId)) {
                    errors.push({
                        field: `attorneys[${i}].representedPartyIds`,
                        rule: 'attorney-party-ref-invalid',
                        message: `Attorney "${att.lastName}" references unknown party ID`,
                        source: 'ours',
                    });
                    break;
                }
            }
        }
    }
    // Self-rep contact (address + email) is required for a genuinely
    // self-represented party (no attorney of record) OR one explicitly declared
    // self-represented by a document's SELF_REP metadata; a represented party with
    // no SELF_REP doc is never asked (R6, JTI cert requirement).
    for (let i = 0; i < parties.length; i++) {
        const party = parties[i];
        if (!party.isSelfRepresented && !docSelfRep.has(party.id))
            continue;
        if (!party.selfRepAddress?.address1 || !party.selfRepAddress?.city
            || !party.selfRepAddress?.state || !party.selfRepAddress?.zip) {
            errors.push({
                field: `parties[${i}].selfRepAddress`,
                rule: 'self-rep-address-required',
                message: `Self-represented party "${party.firstName || party.organizationName || ''}" must have a mailing address`,
                source: 'ours',
            });
        }
        if (!party.selfRepEmail) {
            errors.push({
                field: `parties[${i}].selfRepEmail`,
                rule: 'self-rep-email-required',
                message: `Self-represented party "${party.firstName || party.organizationName || ''}" must have an email address`,
                source: 'ours',
            });
        }
    }
    return { valid: errors.length === 0, errors, warnings: [] };
}
